<div class="container mx-auto px-4" [class.dark]="themeService.isDark()">

    <!-- Calculator Form -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

      <!-- Input Section -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-6">Input Parameters</h2>

        <form [formGroup]="dcfForm" class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

            <!-- Left Column -->
            <div class="space-y-4">

              <!-- TTM EPS -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  EPS (TTM) <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <span class="absolute left-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    formControlName="ttmEps"
                    placeholder="Enter TTM EPS"
                    step="0.0001"
                    min="0"
                    class="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    [class.border-red-500]="dcfForm.get('ttmEps')?.invalid && dcfForm.get('ttmEps')?.touched"
                  />
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">TTM - Trailing 12-months EPS</small>
              </div>

              <!-- Growth Rate -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Annual Growth Rate (%) <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <input
                    type="number"
                    formControlName="growthRate"
                    placeholder="Enter growth rate"
                    step="0.01"
                    min="0"
                    class="w-full pr-8 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    [class.border-red-500]="dcfForm.get('growthRate')?.invalid && dcfForm.get('growthRate')?.touched"
                  />
                  <span class="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
                </div>
                <div *ngIf="showGrowthWarning" class="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                  <div class="flex items-start">
                    <svg class="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>High Growth Warning:</strong> Long-term growth rates above 15% are typically not sustainable. Consider using a more conservative estimate.
                    </div>
                  </div>
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Expected annual growth rate for next 5 or 10 years</small>
              </div>

              <!-- Minimum Annual Rate of Return -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum Annual Rate of Return (%) <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <input
                    type="number"
                    formControlName="minAnnualReturn"
                    placeholder="Enter minimum return"
                    step="0.01"
                    min="0"
                    class="w-full pr-8 pl-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    [class.border-red-500]="dcfForm.get('minAnnualReturn')?.invalid && dcfForm.get('minAnnualReturn')?.touched"
                  />
                  <span class="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 text-sm">%</span>
                </div>
                <div *ngIf="showReturnWarning" class="mt-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
                  <div class="flex items-start">
                    <svg class="w-5 h-5 text-orange-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
                    </svg>
                    <div class="text-sm text-orange-800 dark:text-orange-200">
                      <strong>Low Return Notice:</strong> Returns below 10% may not justify the investment risk. Consider if this meets your investment goals.
                    </div>
                  </div>
                </div>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Your required rate of return (default: 15%)</small>
              </div>

            </div>

            <!-- Right Column -->
            <div class="space-y-4">

              <!-- Future PE Ratio -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Future PE Ratio <span class="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  formControlName="futurePE"
                  placeholder="Future PE"
                  step="1"
                  min="0"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  [class.border-red-500]="dcfForm.get('futurePE')?.invalid && dcfForm.get('futurePE')?.touched"
                />
                <small class="text-gray-500 dark:text-gray-400 mt-1">Default: 2x growth rate ({{ defaultFuturePE }})</small>
              </div>

              <!-- Time Horizon -->
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time Horizon (Years)
                </label>
                <select
                  formControlName="timeHorizon"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="" disabled>Select time horizon</option>
                  <option *ngFor="let option of timeHorizonOptions; trackBy: trackByValue" [value]="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <small class="text-gray-500 dark:text-gray-400 mt-1">Investment time frame for growth projections</small>
              </div>

            </div>

          </div>

          <!-- Calculate Button -->
          <div class="pt-4">
            <button
              type="button"
              class="w-full text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-lg px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              [disabled]="dcfForm.invalid"
              (click)="calculateDCF()">
              Calculate Sticker Price
            </button>
          </div>

          <!-- Clear Button -->
          <div>
            <button
              type="button"
              class="w-full text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 font-medium rounded-lg text-lg px-5 py-3 flex items-center justify-center"
              (click)="clearForm()">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Clear All
            </button>
          </div>

        </form>
      </div>

      <!-- Results Section -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white mb-6">Calculation Results</h2>

        <div *ngIf="!calculationResults" class="text-center py-12">
          <svg class="w-16 h-16 text-gray-300 dark:text-gray-500 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <p class="text-gray-500 dark:text-gray-400 text-lg">Enter parameters and click "Calculate Sticker Price" to see results</p>
        </div>

        <div *ngIf="calculationResults" class="space-y-6">

          <!-- Sticker Price -->
          <div class="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Sticker Price</h3>
            <div class="text-4xl font-bold text-green-600 dark:text-green-400 mb-2">
              ${{ calculationResults.stickerPrice | number:'1.2-2' }}
            </div>
            <p class="text-gray-600 dark:text-gray-400">Fair value based on earnings growth potential</p>
          </div>

          <!-- Buy Price (50% MOS) -->
          <div class="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
            <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Buy Price (50% Discount)</h3>
            <div class="text-4xl font-bold text-orange-600 dark:text-orange-400 mb-2">
              ${{ calculationResults.buyPrice | number:'1.2-2' }}
            </div>
            <p class="text-gray-600 dark:text-gray-400">Discounted buy price to protect against bad estimates</p>
          </div>

          <!-- Calculation Breakdown -->
          <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-4">Calculation Details</h3>
            <div class="space-y-3 text-sm">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Current TTM EPS:</span>
                <span class="font-semibold text-gray-900 dark:text-white">${{ calculationResults.currentEPS | number:'1.2-4' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Growth Rate:</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ calculationResults.growthRate }}%</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Time Horizon:</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ calculationResults.timeHorizon }} years</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Future EPS ({{ calculationResults.timeHorizon }} years):</span>
                <span class="font-semibold text-gray-900 dark:text-white">${{ calculationResults.futureEPS | number:'1.2-4' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Future PE Ratio:</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ calculationResults.futurePE }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Future Stock Price:</span>
                <span class="font-semibold text-gray-900 dark:text-white">${{ calculationResults.futureStockPrice | number:'1.2-2' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Discount Rate:</span>
                <span class="font-semibold text-gray-900 dark:text-white">{{ calculationResults.discountRate }}%</span>
              </div>
              <hr class="my-3 border-gray-300 dark:border-gray-600">
              <div class="flex justify-between font-bold">
                <span class="text-gray-800 dark:text-gray-200">Present Value (Sticker Price):</span>
                <span class="text-green-600 dark:text-green-400">${{ calculationResults.stickerPrice | number:'1.2-2' }}</span>
              </div>
            </div>
          </div>

          <!-- Investment Analysis -->
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
            <h3 class="text-lg font-bold text-blue-900 dark:text-blue-100 mb-3">
              <svg class="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.477.859h4z"></path>
              </svg>Investment Analysis
            </h3>
            <p class="text-blue-800 dark:text-blue-200 mb-3">{{ getInvestmentAnalysis() }}</p>

            <div class="bg-white dark:bg-gray-800 rounded-md p-4 mt-4">
              <h4 class="font-semibold text-gray-800 dark:text-white mb-2">Ben Graham's Margin of Safety Discount</h4>
              <p class="text-gray-700 dark:text-gray-300 text-sm">
                The buy price represents a 50% discount to the calculated sticker price, providing a margin of safety
                to protect against estimation errors and market volatility. This conservative approach helps ensure
                you're buying at a truly attractive price.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Information Section -->
    <div class="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">About the Bad Man Forward Method</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">The Sticker Price Formula</h3>
          <p class="text-gray-600 dark:text-gray-400 mb-4">
            Wall St Yardie's Bad Man Forward method calculates the "Sticker Price" (fair value) - the maximum price you should pay for a stock
            to achieve your minimum acceptable return. The formula projects future earnings and discounts them back to present value.
          </p>
          <div class="bg-gray-50 dark:bg-gray-700 rounded-md p-4">
            <h4 class="font-semibold text-gray-800 dark:text-white mb-2">Key Steps:</h4>
            <ol class="text-gray-600 dark:text-gray-400 text-sm space-y-1 list-decimal list-inside">
              <li>Project future EPS using growth rate</li>
              <li>Estimate future stock price (Future EPS × Future PE)</li>
              <li>Discount back to present value using required return</li>
              <li>Apply 50% discount for buy price</li>
            </ol>
          </div>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Key Assumptions</h3>
          <ul class="text-gray-600 dark:text-gray-400 space-y-2">
            <li class="flex items-start">
              <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span><strong>Growth Rate:</strong>How fast are they growing earnings? Should be realistic and sustainable over time</span>
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span><strong>Future PE:</strong> Defaults to 2× growth rate as this is typically how the market prices stocks</span>
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span><strong>Annual 15% Return:</strong> rate you demand allows you to double your investment in 5 years (Rule of 72).</span>
            </li>
            <li class="flex items-start">
              <svg class="w-5 h-5 text-green-500 mr-2 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
              <span><strong>50% Discount:</strong> Ben Graham's margin of safety principle allowing for mistakes</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
</div>

